import type { AxiosError } from 'axios';
import { Box, Button, Checkbox, Group, ScrollArea, Stack, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SpaceNode } from '@/features/price-lists/spaceTreeUtils';
import {
  getListCoveredSpacesQueryKey,
  useListSpaces,
  useReplaceCoveredSpaces
} from '@/api/generated/work-structure/work-structure';
import { FormModal, ModalFooter, MutationButton, Toast } from '@/components/common';
import { buildTree } from '@/features/price-lists/spaceTreeUtils';

const contractSpaceErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

interface ContractSpaceSelectionModalProps {
  opened: boolean;
  onClose: () => void;
  objectId: string;
  contractId: string;
  currentSpaceIds: string[];
}

const ROW_INDENT = 20;

export const ContractSpaceSelectionModal = ({
  opened,
  onClose,
  objectId,
  contractId,
  currentSpaceIds
}: ContractSpaceSelectionModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data: spacesData } = useListSpaces(objectId, {
    query: { enabled: opened && !!objectId }
  });
  const spaces = useMemo(() => spacesData ?? [], [spacesData]);
  const tree = useMemo(() => buildTree(spaces), [spaces]);

  const childrenMap = useMemo(() => {
    const map = new Map<string, string[]>();
    spaces.forEach((space) => {
      if (space.parentSpaceId && space.id) {
        map.set(space.parentSpaceId, [...(map.get(space.parentSpaceId) ?? []), space.id]);
      }
    });
    return map;
  }, [spaces]);

  const parentMap = useMemo(() => {
    const map = new Map<string, string | undefined>();
    spaces.forEach((space) => {
      if (space.id) map.set(space.id, space.parentSpaceId ?? undefined);
    });
    return map;
  }, [spaces]);

  const collectDescendantIds = (id: string): string[] => {
    const direct = childrenMap.get(id) ?? [];
    return [id, ...direct.flatMap(collectDescendantIds)];
  };

  const collectAncestorIds = (id: string): string[] => {
    const ids: string[] = [];
    let current = parentMap.get(id);
    while (current) {
      ids.push(current);
      current = parentMap.get(current);
    }
    return ids;
  };

  useEffect(() => {
    if (opened) setSelected(new Set(currentSpaceIds));
  }, [opened, currentSpaceIds]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        collectDescendantIds(id).forEach((descendantId) => next.delete(descendantId));
      } else {
        collectDescendantIds(id).forEach((descendantId) => next.add(descendantId));
        collectAncestorIds(id).forEach((ancestorId) => next.add(ancestorId));
      }
      return next;
    });
  };

  const mutation = useReplaceCoveredSpaces({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListCoveredSpacesQueryKey(contractId) });
        onClose();
      }
    }
  });

  const handleSubmit = () => {
    mutation.mutate({ contractId, data: { spaceIds: Array.from(selected) } });
  };

  const renderNode = (node: SpaceNode, depth: number): React.ReactNode => {
    const isSelected = !!node.id && selected.has(node.id);
    return (
      <Box key={node.id}>
        <Group
          gap={8}
          wrap="nowrap"
          py={6}
          pr={16}
          role="button"
          tabIndex={0}
          onClick={() => node.id && toggle(node.id)}
          onKeyDown={(event) => {
            if (node.id && (event.key === 'Enter' || event.key === ' ')) toggle(node.id);
          }}
          style={{
            paddingLeft: 16 + depth * ROW_INDENT,
            cursor: 'pointer',
            borderTop: '1px solid var(--color-border-soft)'
          }}
        >
          <Checkbox
            checked={isSelected}
            onChange={() => node.id && toggle(node.id)}
            onClick={(event) => event.stopPropagation()}
            readOnly
          />
          <Text fz={13.5} style={{ color: 'var(--color-text-primary)' }} truncate>
            {node.name}
            {node.code && (
              <Text span fz={12} style={{ color: 'var(--color-text-muted)' }}>
                {' '}
                ({node.code})
              </Text>
            )}
          </Text>
        </Group>
        {node.children.map((child) => renderNode(child, depth + 1))}
      </Box>
    );
  };

  return (
    <FormModal
      opened={opened}
      onClose={onClose}
      size={520}
      title={
        <Text fz={20} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('contractSpacePicker.title')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <Stack gap={16}>
        <Text fz={12.5} style={{ color: 'var(--color-text-muted)' }}>
          {t('contractSpacePicker.helperText')}
        </Text>

        {mutation.isError && (
          <Toast tone="danger">
            {contractSpaceErrorMessage(mutation.error, t('common.error'))}
          </Toast>
        )}

        <ScrollArea.Autosize
          mah={360}
          style={{
            borderRadius: 8,
            border: '1px solid var(--color-border-soft)'
          }}
        >
          {tree.map((node) => renderNode(node, 0))}
          {tree.length === 0 && (
            <Text p={20} fz={13} style={{ color: 'var(--color-text-muted)' }}>
              {objectId ? t('spaceTree.emptyState') : t('contractSpacePicker.noObjectAssigned')}
            </Text>
          )}
        </ScrollArea.Autosize>

        <ModalFooter>
          <Button variant="outline" onClick={onClose}>
            {t('spaceTree.cancel')}
          </Button>
          <MutationButton
            isPending={mutation.isPending}
            isPaused={mutation.isPaused}
            onClick={handleSubmit}
          >
            {t('contractSpacePicker.submit')}
          </MutationButton>
        </ModalFooter>
      </Stack>
    </FormModal>
  );
};
